// Admin page of a Swiss or round-robin group stage (season → Brackets tab).
// Draft: seeding (+ groups) next to a preview. Started: rounds / groups + match panel.
// Two-stage bracket: a second tab holds the playoffs - a preview with placeholder teams until
// the stage ends, then the playoff bracket itself (EliminationPage, embedded).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AlertModal, ConfirmModal } from '../AlertModal';
import SwissView from './SwissView';
import MajorStageLayout from './MajorStageLayout';
import GroupsView from './GroupsView';
import SeedingEditor from './SeedingEditor';
import MatchPanel from './MatchPanel';
import PlayoffsModal from './PlayoffsModal';
import EliminationPage from './EliminationPage';
import EliminationView from './EliminationView';
import { placeholderTeams } from './bracketText';
import './bracket.css';

export default function StagePage({ bracketData, servers, allTeams, onBack, onReload, onDelete }) {
  const { t } = useTranslation();
  const { bracket, matches = [], teams = {}, groups = [], swiss_standings: standings = [], playoffs: po } = bracketData;
  const [selId, setSelId] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, message: '', onConfirm: null });
  const [busy, setBusy] = useState(false);
  const [playoffs, setPlayoffs] = useState(false);
  const built = !!(po && !po.preview && po.bracket);
  const poStarted = built && po.bracket.status !== 'draft';
  const [tab, setTab] = useState(built ? 'playoffs' : 'stage');
  useEffect(() => { if (built) setTab('playoffs'); }, [built]); // the stage just ended → its playoffs
  useEffect(() => { if (!po) setTab('stage'); }, [po]);

  const isSwiss = bracket.bracket_type === 'swiss';
  const info = bracket.swiss_info || {};
  const major = isSwiss && info.mode === 'major'; // HLTV-style page: playoffs on top, group play graph below
  const gsl = !isSwiss && groups.length > 0 && groups.every(g => g.group_format === 'dual'); // GSL groups (4 teams, double elim)
  const status = bracket.status;
  const draft = status === 'draft';
  const closed = status === 'finished' || status === 'cancelled';
  const selected = selId ? matches.find(m => m.id === selId) : null;
  const errText = (e) => (e.response && e.response.data && e.response.data.error) || e.message;

  const put = async (body) => {
    setBusy(true);
    try { await axios.put(`/api/brackets/${bracket.id}`, body); await onReload(); }
    catch (e) { setAlert({ show: true, message: errText(e), type: 'error' }); }
    finally { setBusy(false); }
  };
  const ask = (message, fn) => setConfirm({ show: true, message, onConfirm: async () => { setConfirm({ show: false }); await fn(); } });
  const swap = async (a, b) => {
    try { await axios.put(`/api/brackets/${bracket.id}/slots/swap`, { a, b }); await onReload(); }
    catch (e) { setAlert({ show: true, message: errText(e), type: 'error' }); }
  };
  const assign = async (teamId, groupIndex) => {
    try { await axios.put(`/api/brackets/${bracket.id}/groups/assign`, { team_id: teamId, group_index: groupIndex }); await onReload(); }
    catch (e) { setAlert({ show: true, message: errText(e), type: 'error' }); }
  };
  const resetBracket = () => ask(t('bracket.confirm.reset', 'Reset the whole bracket to draft? Every result is cleared. Panel matches already created stay, but no longer belong to the bracket.'), async () => {
    setBusy(true);
    try { await axios.put(`/api/brackets/${bracket.id}/reset`); await onReload(); }
    catch (e) { setAlert({ show: true, message: errText(e), type: 'error' }); }
    finally { setBusy(false); }
  });

  // best-of per round / matchday (stage_formats, virtual round = round number)
  const roundCount = isSwiss ? Math.max(info.total_rounds || 0, matches.reduce((mx, m) => Math.max(mx, m.round_number), 0))
    : matches.reduce((mx, m) => Math.max(mx, m.round_number), 0);
  let formats = [];
  try { formats = Array.isArray(bracket.stage_formats) ? bracket.stage_formats : JSON.parse(bracket.stage_formats || '[]'); } catch (_) {}
  const boOf = (r) => {
    // the round's setting first: in a Major-style Swiss a round can mix BO1 and BO3 deciding matches
    const f = formats.find(x => r >= x.from_round && r <= x.to_round);
    if (f) return Number(f.best_of);
    const m = matches.find(x => x.round_number === r && !x.walkover);
    return m ? Number(m.best_of || 1) : 1;
  };
  const setBo = (round, bo) => put({ stage_formats: Array.from({ length: roundCount }, (_, i) => ({ from_round: i + 1, to_round: i + 1, best_of: i + 1 === round ? bo : boOf(i + 1) })) });

  const played = matches.filter(m => m.status === 'completed' && !m.walkover).length;
  const total = matches.filter(m => !m.walkover).length;

  // The playoffs: the bracket itself once built, before that a preview with placeholder teams
  const poNode = !po ? null : built ? (
    <EliminationPage embedded bracketData={po} servers={servers} allTeams={allTeams} onReload={onReload} onBack={() => setTab('stage')} onDelete={() => {}} />
  ) : (<>
    <div className="br-hint" style={{ marginBottom: '0.5rem' }}>
      {t('bracket.po.previewHint', 'Preview - the playoffs fill in from the standings when the stage ends. You then check the seeding and start them.')}
    </div>
    {po.matches.length > 0
      ? <EliminationView bracket={po.bracket} matches={po.matches} teams={placeholderTeams(t, po.teams)} readOnly />
      : <div className="br-hint">{t('bracket.po.tooFew', 'At least 2 teams must go through.')}</div>}
  </>);

  // Stage settings: how it ends + best-of per round (Major: also the deciding matches)
  const settingsBody = (<>
    <div className="br-formats">
      {isSwiss ? (
        <>
          <span className="br-format" title={draft ? '' : t('bracket.thirdDraftOnly', 'Only in a draft')}>
            {t('bracket.swiss.end', 'Ends')}
            <select value={info.mode || 'rounds'} disabled={busy || !draft} onChange={e => put({ swiss_mode: e.target.value })}>
              <option value="rounds">{t('bracket.swiss.modeRounds', 'After fixed rounds')}</option>
              <option value="major">{t('bracket.swiss.modeMajor', 'At X wins / X losses (Major)')}</option>
            </select>
          </span>
          {info.mode === 'major' ? (
            <>
              <span className="br-format">{t('bracket.swiss.wins', 'Wins to qualify')}
                <select value={info.wins} disabled={busy || !draft} onChange={e => put({ swiss_wins_to_qualify: Number(e.target.value) })}>{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}</select></span>
              <span className="br-format">{t('bracket.swiss.losses', 'Losses to be out')}
                <select value={info.losses} disabled={busy || !draft} onChange={e => put({ swiss_losses_to_eliminate: Number(e.target.value) })}>{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}</select></span>
              <span className="br-format" title={t('bracket.swiss.deciderHint', 'Matches where a team can qualify or be eliminated')}>{t('bracket.swiss.decider', 'Deciding matches')}
                <select value={info.decider_bo || 0} disabled={busy || closed} onChange={e => put({ swiss_decider_bo: Number(e.target.value) || null })}>
                  <option value={0}>{t('bracket.swiss.deciderSame', 'Same as the round')}</option>
                  {[1, 3, 5].map(b => <option key={b} value={b}>BO{b}</option>)}
                </select></span>
            </>
          ) : (
            <span className="br-format">{t('bracket.swiss.rounds', 'Rounds')}
              <select value={info.rounds} disabled={busy || !draft} onChange={e => put({ swiss_rounds: Number(e.target.value) })}>{Array.from({ length: 10 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}</select></span>
          )}
        </>
      ) : (
        <span className="br-format" title={draft ? '' : t('bracket.thirdDraftOnly', 'Only in a draft')}>{t('bracket.groups.count', 'Groups')}
          <select value={groups.length} disabled={busy || !draft} onChange={e => put({ num_groups: Number(e.target.value) })}>
            {Array.from({ length: Math.max(1, Math.floor(bracket.team_count / 2)) }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
          </select></span>
      )}
      {gsl && (
        <span className="br-format" title={draft ? t('bracket.gsl.finalHint', "Winners' match winner v decider winner - the winner is the group's 1st place (for stages where one team per group goes through)") : t('bracket.thirdDraftOnly', 'Only in a draft')}>
          {t('bracket.gsl.final', 'Group final')}
          <button type="button" className={`br-toggle${bracket.gsl_group_final ? ' on' : ''}`} disabled={busy || !draft}
            onClick={() => put({ gsl_group_final: !bracket.gsl_group_final })}><span /></button>
        </span>
      )}
      {Array.from({ length: roundCount }, (_, i) => i + 1).map(r => (
        <label key={r} className="br-format">
          {isSwiss ? t('bracket.round.n', 'Round {{n}}', { n: r })
            : gsl ? [t('bracket.gsl.opening', 'Opening round'), t('bracket.gsl.round2', "Winners' / elimination"), t('bracket.gsl.decider', 'Decider match'), t('bracket.gsl.final', 'Group final')][r - 1]
              : t('bracket.round.matchday', 'Matchday {{n}}', { n: r })}
          <select value={boOf(r)} disabled={busy || closed} onChange={e => setBo(r, Number(e.target.value))}>
            {[1, 3, 5].map(b => <option key={b} value={b}>BO{b}</option>)}
          </select>
        </label>
      ))}
    </div>
    <div className="br-hint" style={{ marginTop: '0.4rem' }}>
      {isSwiss ? t('bracket.swiss.hint', 'Each round is paired by itself when the previous one is finished: same records meet, no rematches, byes go round. Drag teams to change a pairing before its matches are created.')
        : gsl ? t('bracket.groups.hintGsl', 'GSL: 4 teams per group, double elimination - opening matches, winners\' match (1st place), elimination match (4th place), decider (2nd / 3rd). No draws.')
          : t('bracket.groups.hint', 'Everyone plays everyone in the group once. A level series is a draw (1 point, a win 3).')}
    </div>
  </>);

  const view = isSwiss
    ? <SwissView bracket={bracket} matches={matches} teams={teams} standings={standings} selectedId={selId} onSelect={m => setSelId(m.id)} onSwap={!closed ? swap : undefined} />
    : <GroupsView bracket={bracket} matches={matches} teams={teams} groups={groups} selectedId={selId} onSelect={m => setSelId(m.id)} onSwap={!closed ? swap : undefined} onAssign={assign} />;

  return (
    <div className="br-page">
      <AlertModal show={alert.show} message={alert.message} type={alert.type} onClose={() => setAlert({ ...alert, show: false })} />
      <ConfirmModal show={confirm.show} message={confirm.message} onConfirm={confirm.onConfirm} onClose={() => setConfirm({ show: false })} />

      <div className="br-head">
        <button className="br-btn ghost" onClick={onBack}>← {t('common.back', 'Back')}</button>
        <div>
          <h2>{bracket.name}</h2>
          <div className="br-sub">
            {isSwiss
              ? (info.mode === 'major'
                ? t('bracket.swiss.subMajor', 'Swiss · {{w}} wins to qualify, {{l}} losses out', { w: info.wins, l: info.losses })
                : t('bracket.swiss.subRounds', 'Swiss · {{n}} rounds', { n: info.rounds }))
              : gsl ? t('bracket.groups.subGsl', 'GSL groups · {{n}} groups', { n: groups.length })
                : t('bracket.groups.sub', 'Round robin · {{n}} groups', { n: groups.length })}
            {' · '}{t('bracket.teamsN', '{{n}} teams', { n: bracket.team_count })}
            {!draft && <> · {t('bracket.playedOf', '{{done}}/{{total}} played', { done: played, total })}</>}
          </div>
        </div>
        <span className={`br-pill ${status}`}>{t(`bracketStatus.${status}`, status)}</span>
        <div className="br-spacer" />
        <div className="br-actions">
          <a className="br-btn ghost" href={`/bracket/${bracket.id}`} target="_blank" rel="noreferrer">{t('bracket.publicPage', 'Public page')} ↗</a>
          {draft && <button className="br-btn green" disabled={busy || matches.length === 0}
            onClick={() => ask(t('bracket.confirm.start', 'Start the bracket? Seeding is locked once it starts.'), () => put({ status: 'active' }))}>▶ {t('bracket.start', 'Start')}</button>}
          {!closed && <button className="br-btn" disabled={busy || poStarted} title={poStarted ? t('bracket.po.startedHint', 'The playoffs have started.') : ''}
            onClick={() => setPlayoffs(true)}>🏆 {po ? t('bracket.po.settings', 'Playoff settings') : t('bracket.po.add', 'Add playoffs')}</button>}
          {(status === 'active' || status === 'completed') && <button className="br-btn ghost" disabled={busy}
            onClick={() => ask(t('bracket.confirm.finish', 'Mark the bracket as finished?'), () => put({ status: 'finished' }))}>{t('bracket.finish', 'Finish')}</button>}
          {!draft && <button className="br-btn amber" disabled={busy} onClick={resetBracket}>{t('bracket.reset', 'Reset to draft')}</button>}
          <button className="br-btn danger" disabled={busy} onClick={() => onDelete(bracket.id)}>{t('common.delete', 'Delete')}</button>
        </div>
      </div>

      {po && !major && (
        <div className="br-stages" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'stage'} className={tab === 'stage' ? 'on' : ''} onClick={() => setTab('stage')}>
            <span className="no">1</span>{isSwiss ? t('bracket.type.swiss', 'Swiss') : t('bracket.type.groups', 'Group stage')}
            {po.stage_done && <span className="br-pill completed">{t('bracket.po.stageDone', 'Finished')}</span>}
          </button>
          <span className="arrow">→</span>
          <button type="button" role="tab" aria-selected={tab === 'playoffs'} className={tab === 'playoffs' ? 'on' : ''} onClick={() => setTab('playoffs')}>
            <span className="no">2</span>{t('bracket.po.title', 'Playoffs')}
            <span className="br-hint">{po.config.bracket_type === 'double_elimination' ? t('bracket.type.double', 'Double elimination') : t('bracket.type.single', 'Single elimination')}</span>
            {built ? <span className={`br-pill ${po.bracket.status}`}>{t(`bracketStatus.${po.bracket.status}`, po.bracket.status)}</span>
              : <span className="br-pill">{t('bracket.po.waiting', 'Waiting for the stage')}</span>}
          </button>
        </div>
      )}

      {tab === 'playoffs' && po && !major ? (built ? poNode : (
        <div className="br-card" style={{ padding: '0.7rem 0.8rem' }}>{poNode}</div>
      )) : (<>

      {!major && <div className="br-card" style={{ padding: '0.6rem 0.8rem' }}>{settingsBody}</div>}

      {major ? (<>
        {draft && <SeedingEditor bracket={bracket} allTeams={allTeams} onChanged={onReload} />}
        <MajorStageLayout playoffs={poNode} settings={<div style={{ marginBottom: '0.6rem' }}>{settingsBody}</div>}
          bracket={bracket} matches={matches} teams={teams} standings={standings}
          selectedId={selId} onSelect={m => setSelId(m.id)} onSwap={!closed ? swap : undefined} />
      </>) : draft ? (
        <div className="br-draft">
          <SeedingEditor bracket={bracket} allTeams={allTeams} onChanged={onReload} />
          <div style={{ minWidth: 0 }}>{view}</div>
        </div>
      ) : view}

      {selected && (
        <MatchPanel bracket={bracket} match={selected} matches={matches} teams={teams} servers={servers}
          onClose={() => setSelId(null)} onChanged={onReload} />
      )}
      </>)}
      {playoffs && (
        <PlayoffsModal isOpen={playoffs} onClose={() => setPlayoffs(false)} bracket={bracket} groups={groups} config={po ? po.config : null}
          onSaved={async () => { setPlayoffs(false); await onReload(); }} />
      )}
    </div>
  );
}
